import { useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { invoices as invoicesApi } from '@/utils/api'
import type { CreditsResponse, Invoice } from '@/types/invoice'
import { computeSkonto, formatCurrency, openAmount, STATUS_CONFIG } from './invoiceUtils'

const EMPTY_CREDITS: CreditsResponse = { credits: [], applied: [] }

/**
 * Zustand und Aktionen des Detail-Modals.
 *
 * Haelt einen Navigations-STAPEL statt einer einzelnen ID: der Sprung von einer
 * Rechnung in ihre Mahnung legt oben auf, der Zurueck-Pfeil nimmt herunter. Vorher
 * wurde der Modal-Inhalt ersetzt und der Weg zurueck war verloren.
 */
export function useInvoiceDetail() {
  const queryClient = useQueryClient()

  const [stack, setStack] = useState<number[]>([])
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loading, setLoading] = useState(false)
  const [credits, setCredits] = useState<CreditsResponse>(EMPTY_CREDITS)
  // Beschriftungen fuer den Breadcrumb — der Stapel haelt nur IDs, der Zurueck-Pfeil
  // soll aber die Rechnungsnummer zeigen, nicht "#3302".
  const [labels, setLabels] = useState<Record<number, string>>({})

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['invoices'] })
  }, [queryClient])

  /** Laedt einen Datensatz und ersetzt den angezeigten Inhalt (ohne Stapel-Wirkung). */
  const load = useCallback(async (id: number) => {
    setLoading(true)
    setCredits(EMPTY_CREDITS)
    try {
      const res = await invoicesApi.getById(id)
      setInvoice(res.data)
      setLabels((m) => ({ ...m, [id]: res.data?.invoice_number || `#${id}` }))
      // Gutschriften nur fuer Eingangsrechnungen — der Endpoint liefert sonst nichts Sinnvolles.
      if (res.data?.type === 'incoming') {
        try {
          const cRes = await fetch(`/api/invoices/${id}/credits`, { credentials: 'include' })
          if (cRes.ok) setCredits(await cRes.json())
        } catch (err) {
          console.error('[useInvoiceDetail] Gutschriften konnten nicht geladen werden:', err)
        }
      }
      return res.data as Invoice
    } catch (err) {
      console.error('[useInvoiceDetail] Detail konnte nicht geladen werden:', err)
      toast.error('Fehler beim Laden der Details')
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  /** Modal frisch oeffnen — setzt den Stapel zurueck. */
  const open = useCallback(async (id: number) => {
    setStack([id])
    await load(id)
  }, [load])

  /** Verknuepften Beleg oeffnen und den bisherigen als Rueckweg behalten. */
  const push = useCallback(async (id: number) => {
    setStack((s) => [...s, id])
    await load(id)
  }, [load])

  const back = useCallback(async () => {
    const next = stack.slice(0, -1)
    if (next.length === 0) return
    setStack(next)
    await load(next[next.length - 1])
  }, [stack, load])

  const close = useCallback(() => {
    setStack([])
    setInvoice(null)
    setCredits(EMPTY_CREDITS)
  }, [])

  const reload = useCallback(async () => {
    if (invoice) await load(invoice.id)
  }, [invoice, load])

  /** Offener Betrag inkl. verrechneter Gutschriften — kann negativ sein (#107). */
  const openSum = openAmount(invoice, credits.applied)

  /* ── Mutationen ───────────────────────────────────────────────── */

  const statusMutation = useMutation({
    mutationFn: ({ id, status, note }: { id: number; status: string; note?: string | null }) =>
      invoicesApi.updateStatus(id, status, note),
    onSuccess: async (_res, { id, status }) => {
      toast.success(`Status geändert: ${STATUS_CONFIG[status]?.label || status}`)
      await load(id)
      invalidate()
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message || 'Fehler beim Statuswechsel'),
  })

  const payMutation = useMutation({
    mutationFn: ({ id, amount }: { id: number; amount: number }) => invoicesApi.pay(id, amount),
    onSuccess: async (res, { id }) => {
      const data = res.data
      toast.success(data.fullyPaid
        ? 'Rechnung als bezahlt markiert'
        : `Teilzahlung erfasst — Restbetrag: ${formatCurrency(data.remaining)}`)
      await load(id)
      invalidate()
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message || 'Fehler beim Erfassen der Zahlung'),
  })

  const importantMutation = useMutation({
    mutationFn: ({ id, important }: { id: number; important: boolean }) =>
      invoicesApi.setImportant(id, important),
    onSuccess: (res, { important }) => {
      toast.success(important ? 'Als wichtig markiert' : 'Markierung entfernt')
      setInvoice((prev) => prev
        ? { ...prev, is_important: res.data?.is_important ?? (important ? 1 : 0) }
        : prev)
      invalidate()
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message || 'Fehler beim Markieren'),
  })

  const markPaidMutation = useMutation({
    mutationFn: ({ reminderId, invoiceIds }: { reminderId: number; invoiceIds: number[] }) =>
      invoicesApi.markInvoicesPaid(reminderId, invoiceIds),
    onSuccess: async (res, { reminderId }) => {
      toast.success(`${res.data.markedCount} Rechnung(en) als bezahlt markiert`)
      if (res.data.allPaid) toast.success('Alle Rechnungen bezahlt — Mahnung als erledigt markiert')
      await load(reminderId)
      invalidate()
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message || 'Fehler beim Markieren'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => invoicesApi.delete(id),
    onSuccess: (res: any) => {
      // Seit 27.07.2026 wird nur die Buchung entfernt — das Dokument bleibt in der
      // Ablage und steht dort wieder als "offen" zur Uebernahme bereit.
      toast.success(res?.data?.released_document_id
        ? 'Aus der Buchhaltung entfernt — Dokument bleibt in der Ablage'
        : 'Rechnung gelöscht')
      close()
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Fehler beim Entfernen'),
  })

  /**
   * Einzelfeld speichern (Inline-Bearbeitung).
   *
   * Die Skonto-Folgewerte muessen mitgerechnet werden, sobald einer ihrer Eingangs-
   * werte angefasst wird — frueher erledigte das der globale Speichern-Knopf.
   */
  const updateField = useCallback(async (field: string, value: string | number | null) => {
    if (!invoice) return
    const payload: Record<string, any> = { [field]: value }

    // Skonto-Folgewerte nachziehen. `skonto_date` ist bewusst NICHT dabei: wer
    // die Frist direkt setzt, will genau die — computeSkonto wuerde sie sofort
    // wieder aus den Skontotagen ueberschreiben.
    if (['gross_amount', 'skonto_percent', 'skonto_days', 'invoice_date'].includes(field)) {
      const merged = { ...invoice, [field]: value } as Invoice
      // Beim Aendern des Satzes soll der Abzug neu gerechnet werden — bei allen
      // anderen Feldern bleibt ein aus dem Beleg gelesener Betrag stehen.
      if (field === 'skonto_percent') merged.skonto_amount = null
      Object.assign(payload, computeSkonto(merged))
    }

    // Optimistisch anzeigen, damit das Feld nicht kurz zurueckspringt.
    const prev = invoice
    setInvoice({ ...invoice, ...payload })
    try {
      await invoicesApi.update(invoice.id, payload)
      const res = await invoicesApi.getById(invoice.id)
      setInvoice(res.data)
      invalidate()
    } catch (err: any) {
      console.error('[useInvoiceDetail] Feld konnte nicht gespeichert werden:', field, err)
      setInvoice(prev)
      toast.error(err?.response?.data?.error || err?.message || 'Fehler beim Speichern')
    }
  }, [invoice, invalidate])

  const applyCredits = useCallback(async (creditNoteIds: number[]) => {
    if (!invoice || creditNoteIds.length === 0) return
    try {
      const res = await fetch(`/api/invoices/${invoice.id}/apply-credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creditNoteIds }),
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      toast.success('Gutschriften angewendet')
      await load(invoice.id)
      invalidate()
    } catch (err) {
      console.error('[useInvoiceDetail] Gutschriften konnten nicht angewendet werden:', err)
      toast.error('Fehler beim Anrechnen der Gutschriften')
    }
  }, [invoice, load, invalidate])

  /** Beschriftung des Belegs, zu dem der Zurueck-Pfeil fuehrt. */
  const parentLabel = stack.length > 1 ? (labels[stack[stack.length - 2]] ?? '') : null

  return {
    stack, invoice, loading, credits, openSum, parentLabel,
    open, push, back, close, reload, load,
    updateField, applyCredits,
    setStatus: (status: string, note?: string | null) =>
      invoice && statusMutation.mutate({ id: invoice.id, status, note }),
    pay: (amount: number) => invoice && payMutation.mutate({ id: invoice.id, amount }),
    toggleImportant: () =>
      invoice && importantMutation.mutate({ id: invoice.id, important: !invoice.is_important }),
    markLinkedPaid: (invoiceIds: number[]) =>
      invoice && markPaidMutation.mutate({ reminderId: invoice.id, invoiceIds }),
    remove: () => invoice && deleteMutation.mutate(invoice.id),
    pending: {
      pay: payMutation.isPending,
      status: statusMutation.isPending,
      markPaid: markPaidMutation.isPending,
      remove: deleteMutation.isPending,
    },
  }
}

export type InvoiceDetailController = ReturnType<typeof useInvoiceDetail>
